// Section editor — per-field editing with status/source, narrative + AI draft,
// approve, and inline images you can DRAG (pointer-based: mouse + touch) to
// anywhere in the section (top, between any fields, bottom), resize (S/M/Full),
// and place two side-by-side. A "Move to" menu is the always-reliable fallback.
import { useEffect, useRef, useState } from "react";
import {
    Sparkles, Save, CheckCircle2, RefreshCw, Loader2, AlertCircle, FlagTriangleRight,
    ImagePlus, GripVertical, Trash2, Pencil, Check, X, Plus, ArrowUp, ArrowDown, Heading, Wand2, History,
} from "lucide-react";
import { toast } from "sonner";
import { api, API, formatApiError } from "@/lib/api";
import Pill from "@/components/Pill";
import {
    DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { FIELD_STATUS_TONE, SECTION_STATUS_TONE, inputCls } from "./shared";
import ImagePanel from "./ImagePanel";

const FIELD_STATUSES = ["Auto-populated", "Manually entered", "Missing / TBC", "Not applicable", "Needs review", "Approved"];
const REWRITE_MODES = [{ k: "tighten", l: "Tighten" }, { k: "house_style", l: "Match house style" }, { k: "rephrase", l: "Rephrase for this building" }];
const END = 999;

export default function SectionEditor({ rid, section, number, images, canEdit, onChange, boilerplate }) {
    const [fields, setFields] = useState([]);
    const [narrative, setNarrative] = useState("");
    const [na, setNa] = useState(false);
    const [busy, setBusy] = useState("");
    const [dragImg, setDragImg] = useState(null);     // id of image being dragged (zones visible while set)
    const [overAnchor, setOverAnchor] = useState(null);
    const fileRef = useRef(null);

    useEffect(() => {
        setFields((section.fields || []).map((f) => ({ ...f })));
        setNarrative(section.narrative || "");
        setNa(!!section.not_applicable);
    }, [section.code]); // eslint-disable-line

    const setField = (i, patch) => setFields((arr) => arr.map((f, idx) => idx === i ? { ...f, ...patch } : f));

    const nf = (section.fields || []).length;
    const secImages = (images || []).filter((i) => i.section_code === section.code)
        .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
    const effAnchor = (img) => {
        let a = img.anchor_index;
        if (a === null || a === undefined) {
            const pos = img.position || "top";
            a = pos === "top" ? 0 : pos === "middle" ? Math.floor(nf / 2) : END;
        }
        return a >= END ? END : Math.max(0, Math.min(a, nf));
    };
    const imgsAt = (anchor) => secImages.filter((i) => effAnchor(i) === anchor);

    async function saveSection() {
        setBusy("save");
        try {
            await api.patch(`/bsc/${rid}/section/${section.code}`, { fields, narrative, not_applicable: na });
            toast.success("Section saved"); onChange?.();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Save failed"); }
        finally { setBusy(""); }
    }

    async function prefill() {
        setBusy("prefill");
        try {
            const { data } = await api.post(`/bsc/${rid}/prefill`, { section: section.code });
            toast.success(`Pre-filled ${data.applied} field(s) from system data`); onChange?.();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Pre-fill failed"); }
        finally { setBusy(""); }
    }

    async function aiDraft() {
        setBusy("ai");
        try {
            const { data } = await api.post(`/bsc/${rid}/section/${section.code}/ai-draft`);
            setNarrative(data.narrative); toast.success("AI draft generated — review before approving"); onChange?.();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "AI drafting unavailable"); }
        finally { setBusy(""); }
    }

    async function aiRewrite(mode, { fieldKey = null, fieldIndex = null } = {}) {
        const text = fieldKey !== null && fieldIndex !== null ? (fields[fieldIndex]?.value || "") : narrative;
        setBusy(`rw-${fieldKey || "narr"}`);
        try {
            const { data } = await api.post(`/bsc/${rid}/section/${section.code}/boilerplate-rewrite`,
                { mode, field_key: fieldKey, text });
            if (fieldKey !== null && fieldIndex !== null) {
                setField(fieldIndex, { value: data.draft, overridden: true, status: fields[fieldIndex]?.status === "Approved" ? "Needs review" : "Manually entered" });
            } else {
                setNarrative(data.draft);
            }
            toast.success("AI rewrite ready — review and save (not approved)");
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "AI rewrite unavailable"); }
        finally { setBusy(""); }
    }

    async function approve() {
        setBusy("approve");
        try { await api.post(`/bsc/${rid}/section/${section.code}/approve`); toast.success("Section approved"); onChange?.(); }
        catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
        finally { setBusy(""); }
    }

    async function flagMissing(f) {
        try {
            await api.post(`/bsc/${rid}/missing`, { item: f.label, section_code: section.code,
                why_it_matters: `Required for BSCR section ${section.code} — ${section.title}.`,
                suggested_source: f.source || "" });
            toast.success("Added to missing-information register");
        } catch (e) { toast.error("Failed"); }
    }

    async function onAddFile(e) {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file) return;
        if (file.size > 10 * 1024 * 1024) { toast.error("Image too large (max 10 MB)"); return; }
        setBusy("img");
        try {
            const fd = new FormData();
            fd.append("file", file); fd.append("section_code", section.code); fd.append("label", ""); fd.append("anchor_index", String(END));
            await api.post(`/bsc/${rid}/images`, fd, { headers: { "Content-Type": "multipart/form-data" } });
            toast.success("Image added — drag it (or use “Move to”) to position it");
            onChange?.();
        } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Upload failed"); }
        finally { setBusy(""); }
    }

    async function moveImage(imgId, anchor) {
        if (!imgId || anchor === null || anchor === undefined) return;
        try { await api.patch(`/bsc/${rid}/images/${imgId}`, { anchor_index: anchor }); onChange?.(); }
        catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Move failed"); }
    }

    // ---- pointer-drag wiring via window listeners (reliable for mouse + touch) ----
    const dragRef = useRef(null);
    const zoneAt = (x, y) => {
        const el = document.elementFromPoint(x, y);
        const z = el && el.closest("[data-anchor]");
        return z ? Number(z.getAttribute("data-anchor")) : null;
    };
    // auto-scroll the nearest scroll container while dragging near its top/bottom edge
    const EDGE = 90, MAXSPEED = 20;
    const findScroller = (el) => {
        let n = el;
        while (n && n !== document.body) {
            const s = getComputedStyle(n);
            if (/(auto|scroll)/.test(s.overflowY) && n.scrollHeight > n.clientHeight + 4) return n;
            n = n.parentElement;
        }
        return document.scrollingElement || document.documentElement;
    };
    const isWindowScroller = (sc) =>
        sc === document.scrollingElement || sc === document.documentElement || sc === document.body;
    function autoScrollTick() {
        const d = dragRef.current;
        if (!d || !d.active || !d.scrollDir) { if (d) d.rafId = null; return; }
        if (isWindowScroller(d.scroller)) window.scrollBy(0, d.scrollDir * d.scrollSpeed);
        else d.scroller.scrollTop += d.scrollDir * d.scrollSpeed;
        d.rafId = requestAnimationFrame(autoScrollTick);
    }
    function updateAutoScroll(y) {
        const d = dragRef.current; if (!d) return;
        const sc = d.scroller;
        let top, bottom;
        if (isWindowScroller(sc)) { top = 0; bottom = window.innerHeight; }
        else { const r = sc.getBoundingClientRect(); top = r.top; bottom = r.bottom; }
        let dir = 0, dist = 0;
        if (y < top + EDGE) { dir = -1; dist = top + EDGE - y; }
        else if (y > bottom - EDGE) { dir = 1; dist = y - (bottom - EDGE); }
        d.scrollDir = dir;
        d.scrollSpeed = dir ? Math.min(MAXSPEED, 4 + (dist / EDGE) * MAXSPEED) : 0;
        if (dir && !d.rafId) d.rafId = requestAnimationFrame(autoScrollTick);
    }
    function onWinMove(e) {
        const d = dragRef.current; if (!d) return;
        if (!d.active) {
            if (Math.hypot(e.clientX - d.x, e.clientY - d.y) < 6) return;
            d.active = true; setDragImg(d.imgId);
        }
        setOverAnchor(zoneAt(e.clientX, e.clientY));
        updateAutoScroll(e.clientY);
    }
    function onWinUp(e) {
        const d = dragRef.current;
        if (d && d.rafId) cancelAnimationFrame(d.rafId);
        window.removeEventListener("pointermove", onWinMove);
        window.removeEventListener("pointerup", onWinUp);
        window.removeEventListener("mousemove", onWinMove);
        window.removeEventListener("mouseup", onWinUp);
        document.body.style.userSelect = "";
        dragRef.current = null;
        setDragImg(null); setOverAnchor(null);
        if (d && d.active) { const a = zoneAt(e.clientX, e.clientY); if (a !== null) moveImage(d.imgId, a); }
    }
    function startDrag(e, imgId) {
        if (!canEdit) return;
        e.preventDefault();
        document.body.style.userSelect = "none";
        dragRef.current = { imgId, x: e.clientX, y: e.clientY, active: false,
            scroller: findScroller(e.currentTarget || e.target), scrollDir: 0, scrollSpeed: 0, rafId: null };
        window.addEventListener("pointermove", onWinMove);
        window.addEventListener("pointerup", onWinUp);
        window.addEventListener("mousemove", onWinMove);
        window.addEventListener("mouseup", onWinUp);
    }

    const anchorOptions = [
        { v: 0, l: "Top of section" },
        ...(section.fields || []).map((f, i) => ({ v: i + 1, l: `After: ${f.label}` })),
        { v: END, l: "Bottom of section" },
    ];

    const renderImgs = (anchor) => {
        const list = imgsAt(anchor);
        if (list.length === 0) return null;
        return (
            <div className="flex flex-wrap gap-3 items-start" data-testid={`bscr-imgrow-${anchor}`}>
                {list.map((img) => (
                    <InlineImageCard key={img.id} rid={rid} img={img} canEdit={canEdit} anchorOptions={anchorOptions}
                        dragging={dragImg === img.id} curAnchor={effAnchor(img)} onMove={(a) => moveImage(img.id, a)}
                        onStartDrag={startDrag} onChange={onChange} />
                ))}
            </div>
        );
    };
    const zoneActive = canEdit && dragImg !== null;

    return (
        <div data-testid={`bscr-section-editor-${section.code}`} className="space-y-4">
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                    <div className="flex items-center gap-2">
                        <span className="grid place-items-center min-w-[28px] h-7 px-1.5 rounded-lg text-[12px] font-bold text-white" style={{ background: "var(--brand-accent,#C75A1B)" }}>{number || section.code}</span>
                        <h2 className="text-[18px] font-bold text-[#1F1F1F]">{section.title}</h2>
                        <Pill tone={SECTION_STATUS_TONE[section.status] || "neutral"}>{section.status}</Pill>
                    </div>
                    {section.intro && <p className="text-[12.5px] text-[#6F6A64] mt-1">{section.intro}</p>}
                </div>
                {canEdit && (
                    <div className="flex items-center gap-2">
                        {section.code !== "A" && (
                            <button data-testid="bscr-add-image" onClick={() => fileRef.current?.click()} disabled={!!busy}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[#E5E0DA] text-[12px] font-semibold text-[#6F6A64] hover:bg-[#F6F4F0]">
                                {busy === "img" ? <Loader2 size={13} className="animate-spin" /> : <ImagePlus size={13} />} Add image
                            </button>
                        )}
                        <button data-testid="bscr-prefill-section" onClick={prefill} disabled={!!busy}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[#E5E0DA] text-[12px] font-semibold text-[#6F6A64] hover:bg-[#F6F4F0]">
                            {busy === "prefill" ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />} Pre-fill
                        </button>
                        <button data-testid="bscr-approve-section" onClick={approve} disabled={!!busy}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-[12px] font-semibold"
                            style={{ borderColor: "#cfe3cc", color: "#3F7D3A", background: "#F1F7EF" }}>
                            <CheckCircle2 size={13} /> Approve
                        </button>
                    </div>
                )}
            </div>
            <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden" onChange={onAddFile} data-testid={`bscr-image-input-${section.code}`} />

            <label className="flex items-center gap-2 text-[12.5px] text-[#1F1F1F]">
                <input data-testid="bscr-section-na" type="checkbox" checked={na} disabled={!canEdit} onChange={(e) => setNa(e.target.checked)} />
                Mark this section as <b>Not applicable</b> to this building
            </label>

            {!na && (
                <div className="space-y-3">
                    {section.code === "A" ? (
                        <ImagePanel rid={rid} sectionCode="A" images={images} canEdit={canEdit}
                            onChange={onChange} title="Cover image" single={false} />
                    ) : (
                        <>
                            {canEdit && secImages.length > 0 && (
                                <p className="text-[11.5px] text-[#9A968F] italic" data-testid="bscr-image-hint">Tip: drag an image by its grip to a highlighted drop zone, resize with S/M/Full (two small/medium images sit side-by-side), or use the “Move to” menu.</p>
                            )}
                            <DropZone active={zoneActive} anchor={0} label="Place at the top" over={overAnchor === 0} />
                            {renderImgs(0)}
                            {fields.map((f, i) => (
                                <div key={f.key}>
                                    <div data-testid={`bscr-field-${f.key}`} className="rounded-xl border border-[#EFEAE4] bg-white p-3">
                                        <div className="flex items-center justify-between gap-2 mb-1.5 flex-wrap">
                                            <label className="text-[12.5px] font-semibold text-[#1F1F1F]">{f.label}</label>
                                            <div className="flex items-center gap-2">
                                                {f.source && <span className="text-[10.5px] text-[#9A968F]">src: {f.source}</span>}
                                                <Pill tone={FIELD_STATUS_TONE[f.status] || "neutral"}>{f.status}</Pill>
                                            </div>
                                        </div>
                                        {f.field_type === "date" ? (
                                            <input type="date" data-testid={`bscr-field-input-${f.key}`} disabled={!canEdit} value={f.value || ""}
                                                onChange={(e) => setField(i, { value: e.target.value, overridden: true, status: f.status === "Missing / TBC" ? "Manually entered" : f.status })} className={inputCls} />
                                        ) : f.field_type === "text" ? (
                                            <input data-testid={`bscr-field-input-${f.key}`} disabled={!canEdit} value={f.value || ""}
                                                onChange={(e) => setField(i, { value: e.target.value, overridden: true, status: f.status === "Missing / TBC" ? "Manually entered" : f.status })} className={inputCls} />
                                        ) : (
                                            <textarea data-testid={`bscr-field-input-${f.key}`} disabled={!canEdit} rows={2} value={f.value || ""}
                                                onChange={(e) => setField(i, { value: e.target.value, overridden: true, status: f.status === "Missing / TBC" ? "Manually entered" : f.status })} className={inputCls} />
                                        )}
                                        {canEdit && (
                                            <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                                                <select data-testid={`bscr-field-status-${f.key}`} value={f.status} onChange={(e) => setField(i, { status: e.target.value })}
                                                    className="text-[11px] border border-[#E5E0DA] rounded-md px-1.5 py-0.5 bg-white text-[#6F6A64]">
                                                    {FIELD_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                                                </select>
                                                <button onClick={() => setField(i, { value: "", status: "Missing / TBC" })} className="text-[11px] text-[#C68A1C] inline-flex items-center gap-1"><AlertCircle size={11} /> Mark TBC</button>
                                                <button data-testid={`bscr-flag-missing-${f.key}`} onClick={() => flagMissing(f)} className="text-[11px] text-[#6F6A64] inline-flex items-center gap-1 hover:text-[var(--brand-accent,#C75A1B)]"><FlagTriangleRight size={11} /> Add to missing register</button>
                                                {boilerplate?.fields?.[f.key] && (
                                                    <>
                                                        <span data-testid={`bscr-tpl-badge-${f.key}`} className="text-[10.5px] text-[#1F6FE5] inline-flex items-center gap-1" title={boilerplate.fields[f.key].title}>Template available</span>
                                                        <AiRewriteMenu testid={`bscr-rewrite-${f.key}`} busy={busy === `rw-${f.key}`}
                                                            onPick={(mode) => aiRewrite(mode, { fieldKey: f.key, fieldIndex: i })} />
                                                    </>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                    <DropZone active={zoneActive} anchor={i + 1} label={`Place after “${f.label}”`} over={overAnchor === i + 1} />
                                    {renderImgs(i + 1)}
                                </div>
                            ))}
                            {fields.length === 0 && <p className="text-[12.5px] italic text-[#9A968F]">This section is narrative-only.</p>}

                            {section.code === "C" && canEdit && (
                                <ExecSummaryPanel rid={rid} current={narrative} onApply={setNarrative}
                                    history={(section.ai_history || []).filter((h) => h.kind === "executive_summary")}
                                    onChange={onChange} />
                            )}

                            {/* Narrative + AI */}
                            <div className="rounded-xl border border-[#EFEAE4] bg-[#FCFBF9] p-3">
                                <div className="flex items-center justify-between mb-1.5">
                                    <span className="text-[12px] font-bold text-[#1F1F1F]">Section narrative</span>
                                    {canEdit && (
                                        <div className="flex items-center gap-2">
                                            {boilerplate?.narratives?.[section.code] && (
                                                <AiRewriteMenu testid="bscr-rewrite-narrative" busy={busy === "rw-narr"}
                                                    onPick={(mode) => aiRewrite(mode)} />
                                            )}
                                            <button data-testid="bscr-ai-draft" onClick={aiDraft} disabled={!!busy}
                                                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-[#EEF3FB] text-[#1F6FE5] hover:bg-[#E0ECFB]">
                                                {busy === "ai" ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />} Draft from system data &amp; evidence
                                            </button>
                                        </div>
                                    )}
                                </div>
                                <textarea data-testid="bscr-narrative" disabled={!canEdit} rows={6} value={narrative} onChange={(e) => setNarrative(e.target.value)}
                                    placeholder="Draft or write the narrative for this section. AI drafts only use linked data and must be reviewed." className={inputCls} />
                                {section.ai_history?.length > 0 && (
                                    <p className="text-[10.5px] text-[#9A968F] mt-1">AI draft generated {section.ai_history.length} time(s) — last by {section.ai_history[section.ai_history.length - 1].by_name || "—"}. Output is editable and requires consultant review.</p>
                                )}
                            </div>
                            <DropZone active={zoneActive} anchor={END} label="Place at the bottom" over={overAnchor === END} />
                            {renderImgs(END)}
                            <HeadingsBlock rid={rid} section={section} canEdit={canEdit} onChange={onChange} />
                        </>
                    )}
                </div>
            )}

            {canEdit && (
                <div className="flex justify-end">
                    <button data-testid="bscr-save-section" onClick={saveSection} disabled={!!busy}
                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-[12.5px] font-semibold text-white" style={{ background: "var(--brand-accent,#C75A1B)" }}>
                        {busy === "save" ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save section
                    </button>
                </div>
            )}
        </div>
    );
}

function DropZone({ active, anchor, label, over }) {
    if (!active) return <div style={{ height: 6 }} />;
    return (
        <div data-testid={`bscr-dropzone-${anchor}`} data-anchor={anchor}
            className={`my-1.5 rounded-lg border-2 border-dashed text-center text-[11px] font-semibold py-3 transition-colors ${over ? "border-[var(--brand-accent,#C75A1B)] bg-[#FBEFE5] text-[var(--brand-accent,#C75A1B)]" : "border-[#E0D8CE] text-[#B8B2AA]"}`}>
            {over ? "⤓ Drop image here" : label}
        </div>
    );
}

const WIDTHS = [{ v: "small", l: "S", px: 200 }, { v: "medium", l: "M", px: 320 }, { v: "full", l: "Full", px: 640 }];

function InlineImageCard({ rid, img, canEdit, anchorOptions, curAnchor, dragging, onMove, onStartDrag, onChange }) {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(img.label || "");
    const widthCfg = WIDTHS.find((w) => w.v === (img.width || "full")) || WIDTHS[2];

    async function saveLabel() {
        try { await api.patch(`/bsc/${rid}/images/${img.id}`, { label: draft }); setEditing(false); onChange?.(); toast.success("Label updated"); }
        catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
    }
    async function setWidth(w) {
        try { await api.patch(`/bsc/${rid}/images/${img.id}`, { width: w }); onChange?.(); }
        catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
    }
    async function remove() {
        if (!window.confirm("Remove this image?")) return;
        try { await api.delete(`/bsc/${rid}/images/${img.id}`); onChange?.(); toast.success("Image removed"); }
        catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
    }

    return (
        <div data-testid={`bscr-image-${img.id}`}
            className={`rounded-xl border bg-white overflow-hidden transition ${dragging ? "opacity-50 border-[var(--brand-accent,#C75A1B)]" : "border-[#E5E0DA]"}`}
            style={{ width: widthCfg.px, maxWidth: "100%" }}>
            {canEdit && (
                <div className="flex items-center gap-1.5 px-2 py-1.5 bg-[#FCFBF9] border-b border-[#EFEAE4]">
                    <div data-testid={`bscr-image-drag-${img.id}`} onPointerDown={(e) => !editing && onStartDrag(e, img.id)}
                        className="flex items-center gap-1.5 cursor-grab active:cursor-grabbing select-none" style={{ touchAction: "none" }}>
                        <GripVertical size={14} className="text-[#A89F94]" />
                        <span className="text-[10px] font-semibold uppercase tracking-wider text-[#9A968F]">Drag</span>
                    </div>
                    <div className="ml-auto flex items-center gap-1">
                        {WIDTHS.map((w) => (
                            <button key={w.v} onClick={() => setWidth(w.v)} data-testid={`bscr-image-width-${w.v}-${img.id}`}
                                className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${(img.width || "full") === w.v ? "text-white" : "text-[#6F6A64] bg-[#F0EDE8]"}`}
                                style={(img.width || "full") === w.v ? { background: "var(--brand-accent,#C75A1B)" } : undefined} title={`Size: ${w.v}`}>{w.l}</button>
                        ))}
                    </div>
                </div>
            )}
            <img src={`${API}/bsc/${rid}/images/${img.id}/file`} alt={img.label || "BSCR image"} className="w-full object-cover" style={{ maxHeight: 240 }} draggable={false} />
            <div className="px-2 py-1.5 flex items-center gap-1.5">
                {editing ? (
                    <>
                        <input autoFocus value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="Label (e.g. North Elevation)"
                            className="flex-1 text-[12px] border border-[#E5E0DA] rounded-md px-2 py-1 outline-none" data-testid={`bscr-image-label-input-${img.id}`}
                            onKeyDown={(e) => e.key === "Enter" && saveLabel()} />
                        <button onClick={saveLabel} data-testid={`bscr-image-label-save-${img.id}`} className="text-[#3F7D3A]"><Check size={15} /></button>
                        <button onClick={() => setEditing(false)} className="text-[#9A968F]"><X size={15} /></button>
                    </>
                ) : (
                    <>
                        <span className="flex-1 text-[12px] text-[#1F1F1F] truncate">{img.label || <span className="italic text-[#9A968F]">No label</span>}</span>
                        {canEdit && (
                            <>
                                <button onClick={() => { setEditing(true); setDraft(img.label || ""); }} data-testid={`bscr-image-edit-${img.id}`} className="text-[#6F6A64] hover:text-[var(--brand-accent,#C75A1B)]"><Pencil size={13} /></button>
                                <button onClick={remove} data-testid={`bscr-image-delete-${img.id}`} className="text-[#B33A2E]"><Trash2 size={13} /></button>
                            </>
                        )}
                    </>
                )}
            </div>
            {canEdit && !editing && (
                <div className="px-2 pb-2 flex items-center gap-1.5">
                    <span className="text-[10.5px] text-[#9A968F]">Move to:</span>
                    <select value={curAnchor} onChange={(e) => onMove(Number(e.target.value))} data-testid={`bscr-image-moveto-${img.id}`}
                        className="flex-1 text-[10.5px] border border-[#EFEAE4] rounded-md px-1.5 py-1 bg-[#FCFBF9] text-[#6F6A64]">
                        {anchorOptions.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
                    </select>
                </div>
            )}
        </div>
    );
}


// ---------- AI Rewrite dropdown (blue→teal AI surface) ----------
export function AiRewriteMenu({ onPick, busy, testid }) {
    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <button data-testid={testid} disabled={busy}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold text-white disabled:opacity-60"
                    style={{ background: "linear-gradient(90deg,#1F6FE5 0%,#0FB5BA 100%)" }}>
                    {busy ? <Loader2 size={12} className="animate-spin" /> : <Wand2 size={12} />} AI Rewrite
                </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="text-[12.5px]">
                <DropdownMenuLabel className="text-[10.5px] uppercase tracking-wide text-[#9A968F]">Rewrite as</DropdownMenuLabel>
                {REWRITE_MODES.map((m) => (
                    <DropdownMenuItem key={m.k} data-testid={`${testid}-${m.k}`} onClick={() => onPick(m.k)}>
                        <Sparkles size={12} className="mr-2 text-[#1F6FE5]" /> {m.l}
                    </DropdownMenuItem>
                ))}
            </DropdownMenuContent>
        </DropdownMenu>
    );
}

// ---------- Executive Summary generator (Phase 4) ----------
function wordDiff(a, b) {
    const A = (a || "").split(/(\s+)/), B = (b || "").split(/(\s+)/);
    const n = A.length, m = B.length;
    const dp = Array.from({ length: n + 1 }, () => new Array(m + 1).fill(0));
    for (let i = n - 1; i >= 0; i--)
        for (let j = m - 1; j >= 0; j--)
            dp[i][j] = A[i] === B[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1]);
    const out = [];
    let i = 0, j = 0;
    while (i < n && j < m) {
        if (A[i] === B[j]) { out.push({ t: "same", v: A[i] }); i++; j++; }
        else if (dp[i + 1][j] >= dp[i][j + 1]) { out.push({ t: "del", v: A[i] }); i++; }
        else { out.push({ t: "add", v: B[j] }); j++; }
    }
    while (i < n) out.push({ t: "del", v: A[i++] });
    while (j < m) out.push({ t: "add", v: B[j++] });
    return out;
}

function ExecSummaryPanel({ rid, current, onApply, history = [], onChange }) {
    const [draft, setDraft] = useState(null);
    const [draftId, setDraftId] = useState(null);
    const [busy, setBusy] = useState(false);
    const [showDiff, setShowDiff] = useState(false);
    const [showHistory, setShowHistory] = useState(false);

    async function generate() {
        setBusy(true);
        try {
            const { data } = await api.post(`/bsc/${rid}/executive-summary`);
            setDraft(data.draft);
            setDraftId(data.draft_id);
            setShowDiff(!!(current || "").trim());
            onChange?.();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Generation unavailable"); }
        finally { setBusy(false); }
    }
    async function apply() {
        try {
            await api.post(`/bsc/${rid}/executive-summary/apply`, { draft_id: draftId, narrative: draft });
            onApply(draft);
            setDraft(null);
            setDraftId(null);
            toast.success("Applied to narrative — review and save (not approved)");
            onChange?.();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not apply draft"); }
    }
    const fmt = (iso) => { try { return new Date(iso).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" }); } catch { return iso; } };

    return (
        <div data-testid="exec-summary-panel" className="rounded-xl border p-3 mb-3"
            style={{ borderColor: "#CFE3FB", background: "linear-gradient(135deg,#F2F8FF 0%,#EFFBFB 100%)" }}>
            <div className="flex items-center justify-between gap-2 flex-wrap">
                <div>
                    <span className="text-[12.5px] font-bold text-[#0E5A8A] flex items-center gap-1.5"><Wand2 size={14} /> Executive Summary generator</span>
                    <p className="text-[11px] text-[#4A6B82]">Drafts a multi-paragraph summary from the Building Description, Accountable Persons, FRAEW/cladding, fire strategy and risk-assessment outcomes.</p>
                </div>
                <button data-testid="exec-summary-generate" onClick={generate} disabled={busy}
                    className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-[12px] font-semibold text-white disabled:opacity-60"
                    style={{ background: "linear-gradient(90deg,#1F6FE5 0%,#0FB5BA 100%)" }}>
                    {busy ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />} {draft ? "Regenerate" : "Generate Executive Summary"}
                </button>
            </div>

            {draft && (
                <div className="mt-3 rounded-lg border border-[#CFE3FB] bg-white p-3">
                    <div className="flex items-center justify-between mb-2">
                        <span className="text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full text-white" style={{ background: "linear-gradient(90deg,#1F6FE5,#0FB5BA)" }}>AI draft · requires review</span>
                        {(current || "").trim() && (
                            <button data-testid="exec-summary-diff-toggle" onClick={() => setShowDiff((v) => !v)}
                                className="text-[11px] font-semibold text-[#1F6FE5]">{showDiff ? "Show draft" : "Diff vs current"}</button>
                        )}
                    </div>
                    {showDiff && (current || "").trim() ? (
                        <p data-testid="exec-summary-diff" className="text-[12.5px] leading-relaxed whitespace-pre-wrap">
                            {wordDiff(current, draft).map((p, idx) => (
                                <span key={idx}
                                    className={p.t === "add" ? "bg-[#D7F5EC] text-[#0B6E4F]" : p.t === "del" ? "bg-[#FBE3E0] text-[#B33A2E] line-through" : "text-[#2b2b2b]"}>{p.v}</span>
                            ))}
                        </p>
                    ) : (
                        <p data-testid="exec-summary-draft" className="text-[12.5px] text-[#2b2b2b] leading-relaxed whitespace-pre-wrap">{draft}</p>
                    )}
                    <div className="flex justify-end gap-2 mt-3">
                        <button onClick={() => { setDraft(null); setDraftId(null); }} className="px-3 py-1.5 rounded-lg border border-[#E5E0DA] text-[12px] font-semibold text-[#6F6A64]">Discard</button>
                        <button data-testid="exec-summary-apply" onClick={apply} className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-[12px] font-semibold text-white" style={{ background: "var(--brand-accent,#C75A1B)" }}><Check size={13} /> Apply to narrative</button>
                    </div>
                </div>
            )}

            {history.length > 0 && (
                <div className="mt-3">
                    <button data-testid="exec-summary-history-toggle" onClick={() => setShowHistory((v) => !v)}
                        className="text-[11px] font-semibold text-[#0E5A8A] inline-flex items-center gap-1">
                        <History size={12} /> AI draft history ({history.length}) {showHistory ? "▲" : "▼"}
                    </button>
                    {showHistory && (
                        <ul data-testid="exec-summary-history" className="mt-2 space-y-2">
                            {[...history].reverse().map((h) => (
                                <li key={h.id} data-testid={`exec-summary-history-${h.id}`}
                                    className="rounded-lg border border-[#CFE3FB] bg-white/70 p-2.5">
                                    <div className="flex items-center justify-between gap-2 mb-1">
                                        <span className="text-[10.5px] font-semibold text-[#4A6B82]">Generated by {h.by_name || "—"} · {fmt(h.at)}</span>
                                        {h.accepted
                                            ? <span className="text-[9.5px] font-bold uppercase px-1.5 py-0.5 rounded-full bg-[#D7F5EC] text-[#0B6E4F]">Applied{h.applied_by_name ? ` · ${h.applied_by_name}` : ""}</span>
                                            : <span className="text-[9.5px] font-bold uppercase px-1.5 py-0.5 rounded-full bg-[#FBEFD9] text-[#9A6A12]">Draft only</span>}
                                    </div>
                                    <p className="text-[11.5px] text-[#3e4b63] leading-relaxed whitespace-pre-wrap line-clamp-4">{h.output}</p>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            )}
        </div>
    );
}

// ---------- Custom heading blocks (Phase 2) ----------
function HeadingsBlock({ rid, section, canEdit, onChange }) {
    const headings = [...(section.headings || [])].sort((a, b) => (a.order || 0) - (b.order || 0));
    const [busy, setBusy] = useState(false);

    async function add() {
        setBusy(true);
        try { await api.post(`/bsc/${rid}/section/${section.code}/headings`, { title: "New heading" }); toast.success("Heading added"); onChange?.(); }
        catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
        finally { setBusy(false); }
    }
    async function move(hid, dir) {
        const ids = headings.map((h) => h.id);
        const i = ids.indexOf(hid), j = dir === "up" ? i - 1 : i + 1;
        if (j < 0 || j >= ids.length) return;
        [ids[i], ids[j]] = [ids[j], ids[i]];
        try { await api.post(`/bsc/${rid}/section/${section.code}/headings/reorder`, { ordered_ids: ids }); onChange?.(); }
        catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
    }

    if (!canEdit && headings.length === 0) return null;
    return (
        <div data-testid="bscr-headings" className="space-y-2">
            <div className="flex items-center justify-between">
                <span className="text-[12px] font-bold text-[#1F1F1F] flex items-center gap-1.5"><Heading size={14} className="text-[var(--brand-accent,#C75A1B)]" /> Headings</span>
                {canEdit && (
                    <button data-testid="bscr-add-heading" onClick={add} disabled={busy}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border border-[#E5E0DA] text-[11.5px] font-semibold text-[#6F6A64] hover:bg-[#F6F4F0]">
                        {busy ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />} Add heading
                    </button>
                )}
            </div>
            {headings.map((h, idx) => (
                <HeadingCard key={h.id} rid={rid} code={section.code} heading={h} canEdit={canEdit} onChange={onChange}
                    onMove={move} isFirst={idx === 0} isLast={idx === headings.length - 1} />
            ))}
            {headings.length === 0 && <p className="text-[11.5px] italic text-[#9A968F]">No custom headings yet. Add a heading to group extra fields within this section.</p>}
        </div>
    );
}

function HeadingCard({ rid, code, heading, canEdit, onChange, onMove, isFirst, isLast }) {
    const [editing, setEditing] = useState(false);
    const [title, setTitle] = useState(heading.title || "");
    const [confirmDel, setConfirmDel] = useState(false);
    const fields = heading.fields || [];

    async function saveTitle() {
        setEditing(false);
        if (title === heading.title) return;
        try { await api.patch(`/bsc/${rid}/section/${code}/headings/${heading.id}`, { title }); onChange?.(); }
        catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
    }
    async function remove() {
        const approved = fields.some((f) => f.status === "Approved");
        try { await api.delete(`/bsc/${rid}/section/${code}/headings/${heading.id}${approved ? "?force=true" : ""}`); toast.success("Heading deleted"); onChange?.(); }
        catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
        finally { setConfirmDel(false); }
    }
    async function addField() {
        try { await api.post(`/bsc/${rid}/section/${code}/headings/${heading.id}/fields`, { label: "New field" }); onChange?.(); }
        catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
    }
    async function saveFieldValue(key, value) {
        try { await api.put(`/bsc/${rid}/section/${code}/field`, { key, value, overridden: true }); onChange?.(); }
        catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
    }

    return (
        <div data-testid={`bscr-heading-${heading.id}`} className="rounded-xl border border-[#EFEAE4] bg-[#FCFBF9] p-3">
            <div className="flex items-center gap-2">
                {editing ? (
                    <input autoFocus value={title} data-testid={`bscr-heading-title-input-${heading.id}`}
                        onChange={(e) => setTitle(e.target.value)} onBlur={saveTitle}
                        onKeyDown={(e) => { if (e.key === "Enter") saveTitle(); if (e.key === "Escape") setEditing(false); }}
                        className="flex-1 text-[13px] font-bold border border-[var(--brand-accent,#C75A1B)] rounded px-1.5 py-0.5 outline-none" />
                ) : (
                    <span className="flex-1 text-[13px] font-bold text-[#1F1F1F]">{heading.title || "Untitled heading"}</span>
                )}
                {canEdit && !editing && (
                    <div className="flex items-center gap-1">
                        <button data-testid={`bscr-heading-up-${heading.id}`} disabled={isFirst} onClick={() => onMove(heading.id, "up")} className="text-[#9A968F] hover:text-[#1F1F1F] disabled:opacity-30"><ArrowUp size={14} /></button>
                        <button data-testid={`bscr-heading-down-${heading.id}`} disabled={isLast} onClick={() => onMove(heading.id, "down")} className="text-[#9A968F] hover:text-[#1F1F1F] disabled:opacity-30"><ArrowDown size={14} /></button>
                        <button data-testid={`bscr-heading-rename-${heading.id}`} onClick={() => { setTitle(heading.title || ""); setEditing(true); }} className="text-[#6F6A64] hover:text-[var(--brand-accent,#C75A1B)]"><Pencil size={13} /></button>
                        <button data-testid={`bscr-heading-delete-${heading.id}`} onClick={() => setConfirmDel(true)} className="text-[#B33A2E]"><Trash2 size={13} /></button>
                    </div>
                )}
            </div>
            {fields.length > 0 && (
                <div className="mt-2 space-y-2">
                    {fields.map((f) => (
                        <div key={f.key} data-testid={`bscr-hfield-${f.key}`}>
                            <label className="text-[11.5px] font-semibold text-[#1F1F1F]">{f.label}</label>
                            <textarea defaultValue={f.value || ""} disabled={!canEdit} rows={2} data-testid={`bscr-hfield-input-${f.key}`}
                                onBlur={(e) => canEdit && e.target.value !== (f.value || "") && saveFieldValue(f.key, e.target.value)}
                                className={inputCls} />
                        </div>
                    ))}
                </div>
            )}
            {canEdit && (
                <button data-testid={`bscr-heading-add-field-${heading.id}`} onClick={addField}
                    className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold text-[#6F6A64] hover:text-[var(--brand-accent,#C75A1B)]"><Plus size={12} /> Add field</button>
            )}
            {confirmDel && (
                <div className="mt-2 flex items-center gap-2 text-[11.5px]">
                    <span className="text-[#B33A2E] font-semibold">Delete this heading?</span>
                    <button data-testid={`bscr-heading-confirm-del-${heading.id}`} onClick={remove} className="px-2 py-0.5 rounded bg-[#B33A2E] text-white font-semibold">Delete</button>
                    <button onClick={() => setConfirmDel(false)} className="px-2 py-0.5 rounded border border-[#E5E0DA] text-[#6F6A64]">Cancel</button>
                </div>
            )}
        </div>
    );
}
